import { connectReconnectingSocket, buildWebSocketUrl } from './reconnectingSocket';
import { encodeBase64Url } from '../crypto/primitives';

export const QuickShareMessageTypes = {
  SessionCreated: 'quickShare.sessionCreated',
  GuestClaimed: 'quickShare.guestClaimed',
  DeliverPackage: 'quickShare.deliverPackage',
  PackageReady: 'quickShare.packageReady',
  PackageDelivered: 'quickShare.packageDelivered',
  Waiting: 'quickShare.waiting',
  Cancelled: 'quickShare.cancelled',
  Error: 'quickShare.error',
} as const;

export interface QuickShareOwnerSession {
  close: () => void;
  sendPackage: (payload: {
    sessionId: string;
    guestPath: string;
    wrappedPackageKey: string;
    ciphertext: string;
  }) => void;
}

export function connectQuickShareOwner(options: {
  folderId: string;
  onSessionCreated: (msg: { sessionId: string; code: string; display: string }) => void;
  onGuestClaimed: (msg: { sessionId: string; guestPublicKey: string }) => void;
  onPackageDelivered: (sessionId: string) => void;
  onCancelled: (reason: string) => void;
  onError: (code: string) => void;
  onStateChange?: (state: 'connecting' | 'connected' | 'disconnected') => void;
}): QuickShareOwnerSession {
  const url = buildWebSocketUrl(`/api/ws/quick-share?folderId=${encodeURIComponent(options.folderId)}`);
  let closed = false;
  let raw: WebSocket | null = null;

  const connect = () => {
    if (closed) return;
    options.onStateChange?.('connecting');
    raw = new WebSocket(url);
    raw.addEventListener('open', () => {
      options.onStateChange?.('connected');
    });
    raw.addEventListener('message', (event) => {
      if (event.data === 'ping' || event.data === '{"type":"ping"}') {
        raw?.send('{"type":"pong"}');
        return;
      }
      try {
        const payload = JSON.parse(String(event.data)) as {
          type?: string;
          sessionId?: string;
          code?: string;
          display?: string;
          guestPublicKey?: string;
          reason?: string;
        };
        if (payload.type === 'ping') {
          raw?.send(JSON.stringify({ type: 'pong' }));
          return;
        }
        if (payload.type === 'pong') return;
        switch (payload.type) {
          case QuickShareMessageTypes.SessionCreated:
            if (payload.sessionId && payload.code && payload.display) {
              options.onSessionCreated({
                sessionId: payload.sessionId,
                code: payload.code,
                display: payload.display,
              });
            }
            break;
          case QuickShareMessageTypes.GuestClaimed:
            if (payload.sessionId && payload.guestPublicKey) {
              options.onGuestClaimed({
                sessionId: payload.sessionId,
                guestPublicKey: payload.guestPublicKey,
              });
            }
            break;
          case QuickShareMessageTypes.PackageDelivered:
            if (payload.sessionId) {
              options.onPackageDelivered(payload.sessionId);
            }
            break;
          case QuickShareMessageTypes.Cancelled:
            options.onCancelled(payload.reason ?? 'cancelled');
            break;
          case QuickShareMessageTypes.Error:
            options.onError(payload.code ?? 'error');
            break;
          default:
            break;
        }
      } catch {
        // ignore malformed frames
      }
    });
    raw.addEventListener('close', () => {
      options.onStateChange?.('disconnected');
      // Do not reconnect — each owner connect creates a new share code.
    });
  };

  connect();

  return {
    close: () => {
      closed = true;
      raw?.close();
      raw = null;
    },
    sendPackage: (payload) => {
      raw?.send(
        JSON.stringify({
          type: QuickShareMessageTypes.DeliverPackage,
          sessionId: payload.sessionId,
          guestPath: payload.guestPath,
          wrappedPackageKey: payload.wrappedPackageKey,
          ciphertext: payload.ciphertext,
        }),
      );
    },
  };
}

export async function claimQuickShareCode(code: string, guestPublicKey: Uint8Array): Promise<{
  sessionId: string;
  guestToken: string;
}> {
  const response = await fetch('/api/quick-share/claim', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      code,
      guestPublicKey: encodeBase64Url(guestPublicKey),
    }),
  });
  if (!response.ok) {
    let errorCode = 'claim_failed';
    try {
      const body = (await response.json()) as { code?: string };
      if (body.code) errorCode = body.code;
    } catch {
      // ignore
    }
    throw new Error(errorCode);
  }
  return (await response.json()) as { sessionId: string; guestToken: string };
}

export function connectQuickShareGuest(options: {
  sessionId: string;
  guestToken: string;
  onWaiting: () => void;
  onPackageReady: (payload: {
    sessionId: string;
    guestPath: string;
    wrappedPackageKey: string;
    ciphertext: string;
  }) => void;
  onCancelled: (reason: string) => void;
  onStateChange?: (state: 'connecting' | 'connected' | 'disconnected') => void;
}): { close: () => void } {
  const url = buildWebSocketUrl(
    `/api/ws/quick-share/guest?sessionId=${encodeURIComponent(options.sessionId)}&guestToken=${encodeURIComponent(options.guestToken)}`,
  );
  return connectReconnectingSocket({
    url,
    onStateChange: options.onStateChange,
    onMessage: (data) => {
      const msg = data as {
        type?: string;
        sessionId?: string;
        guestPath?: string;
        wrappedPackageKey?: string;
        ciphertext?: string;
        reason?: string;
      };
      switch (msg.type) {
        case QuickShareMessageTypes.Waiting:
          options.onWaiting();
          break;
        case QuickShareMessageTypes.PackageReady:
          if (msg.sessionId && msg.guestPath && msg.wrappedPackageKey && msg.ciphertext) {
            options.onPackageReady({
              sessionId: msg.sessionId,
              guestPath: msg.guestPath,
              wrappedPackageKey: msg.wrappedPackageKey,
              ciphertext: msg.ciphertext,
            });
          }
          break;
        case QuickShareMessageTypes.Cancelled:
          options.onCancelled(msg.reason ?? 'cancelled');
          break;
        default:
          break;
      }
    },
  });
}
